import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, Bell, CheckCheck, Megaphone, X } from "lucide-react";
import { Sheet } from "@/components/forwarding/Sheet";
import { Button } from "@/components/ui/button";
import {
  useDismissNotification,
  useInbox,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from "@/lib/hooks/useNotifications";
import { badgeLabel, type InboxItem } from "@/lib/notifications";
import { safeImageSrc, storagePublicPrefix } from "@/lib/safe-markdown";
import { cn } from "@/lib/utils";
import { SafeMarkdown } from "./SafeMarkdown";

const IMAGE_PREFIX = storagePublicPrefix(import.meta.env["VITE_SUPABASE_URL"] as string | undefined);

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

function InboxEntry({ item, onClose }: { item: InboxItem; onClose: () => void }): JSX.Element {
  const markRead = useMarkNotificationRead();
  const dismiss = useDismissNotification();
  const [open, setOpen] = useState(false);
  const image = item.imageUrl ? safeImageSrc(item.imageUrl, IMAGE_PREFIX) : null;
  const Icon = item.isAlert ? AlertTriangle : Megaphone;

  const toggle = (): void => {
    setOpen((v) => !v);
    if (!item.read) markRead.mutate(item.id);
  };

  return (
    <li className={cn("rounded-xl border p-4", item.isActiveAlert ? "border-destructive/40 bg-destructive/5" : "border-border bg-card")}>
      <div className="flex items-start gap-3">
        <Icon size={16} className={cn("mt-0.5 shrink-0", item.isAlert ? "text-destructive" : "text-primary")} aria-hidden="true" />
        <button type="button" onClick={toggle} aria-expanded={open} className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded">
          <p className={cn("text-sm", item.read ? "text-muted-foreground" : "font-semibold text-foreground")}>
            {!item.read && <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-primary" aria-label="Não lida" />}
            {item.title}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {formatDate(item.publishedAt)}
            {item.isAlert && !item.isActiveAlert && " · resolvido"}
          </p>
        </button>
        <button
          type="button"
          onClick={() => dismiss.mutate(item.id)}
          aria-label={`Dispensar: ${item.title}`}
          className="rounded-md p-1 text-muted-foreground hover:bg-[hsl(var(--surface-raised))] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X size={14} />
        </button>
      </div>
      {open && (
        <div className="mt-3 space-y-3 pl-7">
          {image && <img src={image} alt="" loading="lazy" referrerPolicy="no-referrer" className="w-full rounded-lg border border-border" />}
          {/* Alertas carregam texto de terceiros (ex.: nome do grupo): texto puro. */}
          {item.isAlert ? (
            <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{item.body}</p>
          ) : (
            <SafeMarkdown source={item.body} />
          )}
          {item.actionUrl && (
            <Button asChild size="sm" variant="secondary">
              <Link to={item.actionUrl} onClick={onClose}>Abrir</Link>
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

/** Sino do header com contador + inbox lateral (novidades e alertas). */
export function NotificationBell(): JSX.Element {
  const [open, setOpen] = useState(false);
  const { data: inbox, error } = useInbox();
  const markAll = useMarkAllNotificationsRead();
  const badge = badgeLabel(inbox?.unreadCount ?? 0);
  const close = (): void => setOpen(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={badge ? `Notificações, ${inbox?.unreadCount} não lidas` : "Notificações"}
        className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-surface-raised text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Bell size={16} />
        {badge && (
          <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-primary px-1 text-center text-[10px] font-bold leading-[18px] text-primary-foreground">
            {badge}
          </span>
        )}
      </button>
      <Sheet
        open={open}
        title="Notificações"
        description="Novidades do LucraLink e alertas das suas integrações."
        onClose={close}
        footer={
          <Button type="button" variant="ghost" size="sm" disabled={!inbox?.unreadCount || markAll.isPending} onClick={() => markAll.mutate()}>
            <CheckCheck size={14} aria-hidden="true" />
            Marcar todas como lidas
          </Button>
        }
      >
        {error && <p role="alert" className="text-sm text-destructive">Não foi possível carregar as notificações.</p>}
        {inbox && inbox.items.length === 0 && (
          <p className="py-10 text-center text-sm text-muted-foreground">Nada por aqui. Avisamos quando tiver novidade.</p>
        )}
        {inbox && inbox.items.length > 0 && (
          <ul className="space-y-3">
            {inbox.items.map((item) => <InboxEntry key={item.id} item={item} onClose={close} />)}
          </ul>
        )}
      </Sheet>
    </>
  );
}
